import { useLocation } from "@tanstack/react-router";

/**
 * Detects if the current context is embed mode based on URL search parameters
 * This utility function can be used in contexts where React hooks are not available
 */
export function isEmbedMode(): boolean {
  if (typeof window === "undefined") return false;

  const urlParams = new URLSearchParams(window.location.search);
  const embedParam = urlParams.get("embed");
  return embedParam === "true";
}

/**
 * React hook version that reactively detects embed mode based on router location
 * Use this in React components to get automatic re-renders when the embed state changes
 */
export function useEmbedMode(): boolean {
  const location = useLocation();
  const search = location.search as { embed?: boolean };
  return search.embed === true;
}

/**
 * Gets the color scheme override from URL search parameters
 * This utility function can be used in contexts where React hooks are not available
 */
export function getColorSchemeOverride(): "light" | "dark" | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const colorSchemeParam = urlParams.get("colorScheme");
  return colorSchemeParam === "light" || colorSchemeParam === "dark" ? colorSchemeParam : null;
}

/**
 * Gets the width override from URL search parameters
 * This utility function can be used in contexts where React hooks are not available
 */
export function getWidthOverride(): "mobile" | "tablet" | number | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const widthParam = urlParams.get("width");

  if (!widthParam) return null;

  // Check for preset values
  if (widthParam === "mobile" || widthParam === "tablet") {
    return widthParam;
  }

  // Try to parse as number
  const numericWidth = Number.parseInt(widthParam, 10);
  if (!Number.isNaN(numericWidth) && numericWidth >= 320 && numericWidth <= 1000) {
    return numericWidth;
  }

  return null;
}

/**
 * Gets the height override from URL search parameters
 * This utility function can be used in contexts where React hooks are not available
 */
export function getHeightOverride(): "mobile" | "tablet" | number | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const heightParam = urlParams.get("height");

  if (!heightParam) return null;

  // Check for preset values
  if (heightParam === "mobile" || heightParam === "tablet") {
    return heightParam;
  }

  // Try to parse as number
  const numericHeight = Number.parseInt(heightParam, 10);
  if (!Number.isNaN(numericHeight) && numericHeight >= 480 && numericHeight <= 1200) {
    return numericHeight;
  }

  return null;
}

/**
 * Detects if the app is running in an iframe (embedded context)
 */
export function isInIframe(): boolean {
  if (typeof window === "undefined") return false;

  return window !== window.top;
}
